import { Fragment, useMemo } from 'react';

import { Link, useMatches } from 'react-router-dom';

import { useTranslation } from '@tankef/i18n';
import { ChevronRightIcon } from '@tankef/icons';

import { usePageHeaderOverride, type Breadcrumb } from '../page-header';
import { isRoutePageMeta, type RoutePageMeta } from '../page-meta';

/**
 * Walk the matches deepest-first and return the first `handle` that
 * conforms to `RoutePageMeta`. This is the route the current URL maps
 * to; parent routes (layouts / guards) without metadata are skipped.
 */
function findRouteMeta(matches: ReturnType<typeof useMatches>): RoutePageMeta | undefined {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle;
    if (isRoutePageMeta(handle)) return handle;
  }
  return undefined;
}

/**
 * Resolves the active page header from two sources, in priority order:
 *   1. Dynamic override from `usePageHeader(...)` (detail pages whose
 *      title depends on runtime data).
 *   2. Static metadata declared as `handle: RoutePageMeta` on the route
 *      descriptor — read via `useMatches()` (plan 06 L3).
 *
 * If neither is present, nothing is rendered.
 */
export function PageTitleArea() {
  const { t } = useTranslation();
  const matches = useMatches();
  const override = usePageHeaderOverride();

  const { title, breadcrumbs } = useMemo(() => {
    const meta = findRouteMeta(matches);

    const routeTitle = meta?.titleKey
      ? t(meta.titleKey, { ns: meta.namespace ?? 'common' })
      : undefined;

    const routeCrumbs: Breadcrumb[] = [];
    if (meta?.breadcrumbs) {
      for (const crumb of meta.breadcrumbs) {
        routeCrumbs.push({
          label: t(crumb.labelKey, { ns: crumb.namespace ?? 'common' }),
          href: crumb.href,
        });
      }
    }
    // Append current page as the last (non-linked) crumb when there are parents.
    if (routeTitle && routeCrumbs.length > 0) {
      routeCrumbs.push({ label: routeTitle });
    }

    return {
      title: override.title ?? (meta?.hideTitle ? undefined : routeTitle),
      breadcrumbs: override.breadcrumbs ?? (routeCrumbs.length > 0 ? routeCrumbs : undefined),
    };
  }, [matches, override.title, override.breadcrumbs, t]);

  // Cuando una page declara variant="flow", se considera que renderiza su
  // propio banner via <PageHeader variant="flow"> y el title-area del dashboard
  // no debe duplicarlo.
  if (override.variant === 'flow') return null;

  if (!title && (!breadcrumbs || breadcrumbs.length === 0)) return null;

  const { actions, leading } = override;

  return (
    <div className="hidden min-w-0 items-center gap-4 lg:flex">
      {leading && <div className="flex shrink-0 items-center">{leading}</div>}
      <div className="flex min-w-0 flex-col justify-center">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav
            className="flex items-center gap-1 text-xs text-tkf-fg-muted"
            aria-label="Breadcrumb"
          >
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return (
                <Fragment key={`${crumb.label}-${idx}`}>
                  {idx > 0 && <ChevronRightIcon className="h-3 w-3 text-tkf-fg-subtle" />}
                  {crumb.href && !isLast ? (
                    <Link to={crumb.href} className="truncate hover:text-tkf-fg">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="truncate text-tkf-fg">{crumb.label}</span>
                  )}
                </Fragment>
              );
            })}
          </nav>
        )}
        {title && (
          <h1 className="truncate text-base font-semibold text-tkf-fg lg:text-lg">{title}</h1>
        )}
      </div>

      {/* Route-injected actions (e.g. primary CTA for the current page) */}
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
